// src/v2/components/modules/Reports/BIR0619E/BIR0619EModule.js
import React, { useState, useCallback } from 'react';
import { useFilterManager } from '../../../../hooks/useFilterManager';
import { DynamicFilterBar } from '../../../filters/DynamicFilterBar';
import { LoadingSpinner } from '../../../../../common/Loading';
import { Button } from '../../../../../common/APICommon';

/**
 * Reusable Report Select Component
 * Handles the dynamic text color logic (gray-400 for placeholder, gray-700 for value)
 */
const ReportSelect = ({ label, value, onChange, options, placeholder }) => {
  const hasValue = value !== "" && value !== null && value !== undefined;
  const safeOptions = options || [];

  return (
    <div className="flex flex-col w-full">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <select
        className={`h-[34px] border rounded px-2 text-sm border-gray-300 transition-colors focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none
          ${hasValue ? "text-gray-700 font-semibold" : "text-gray-400"}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {placeholder && <option value="" className="text-gray-400">{placeholder}</option>}
        {safeOptions.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
};

export const BIR0619EModule = ({ reportHook }) => {
  const currentYear = new Date().getFullYear();

  // Local States for Period
  const [month, setMonth] = useState("");
  const [year, setYear] = useState(currentYear.toString());

  // Dynamic Filter Manager for Company
  const {
    activeFilterState,
    onFilterChange,
    apiFilterPayload,
    resetFilters,
    loadingEnums
  } = useFilterManager(['COMPANY']);

  /**
   * Resets both local period states and the FilterManager
   */
  const handleClear = useCallback(() => {
    resetFilters();
    setMonth("");
    setYear(currentYear.toString());
  }, [resetFilters, currentYear]);

  /**
   * Orchestrates the Fetch + Download flow
   */
  const handleGenerate = () => {
    // 1. Validation
    if (!activeFilterState.COMPANY || !month || !year) {
      alert("Please select a Company, Month, and Year to generate the report.");
      return;
    }

    // 2. Prepare API parameters
    const queryParams = {
      companyId: activeFilterState.COMPANY.id,
      month: month,
      year: year,
      filters: JSON.stringify(apiFilterPayload)
    };

    // 3. Prepare Company Info for the PDF (TIN/Address from the selected company object)
    // Note: We use .name or .fullName depending on your API structure
    const companyInfo = {
      name: activeFilterState.COMPANY.name || activeFilterState.COMPANY.fullName || "",
      tin: activeFilterState.COMPANY.tin || "",
      address: activeFilterState.COMPANY.address || ""
    };

    // 4. Trigger Hook Logic
    reportHook.generateAndDownload(queryParams, month, year, companyInfo);
  };

  // Select Options Data
  const monthsOptions = [
    { label: "January", value: "01" },
    { label: "February", value: "02" },
    { label: "March", value: "03" },
    { label: "April", value: "04" },
    { label: "May", value: "05" },
    { label: "June", value: "06" },
    { label: "July", value: "07" },
    { label: "August", value: "08" },
    { label: "September", value: "09" },
    { label: "October", value: "10" },
    { label: "November", value: "11" },
    { label: "December", value: "12" }
  ];

  const yearsOptions = Array.from({ length: 10 }, (_, i) => {
    const y = (currentYear - i).toString();
    return { label: y, value: y };
  });

  return (
    <div className="ReportsInfo shadow-xl bg-white rounded-lg p-6 border border-gray-100">

      {/* Breadcrumb Section */}
      <div className='flex items-center text-sm mb-2'>
        <span className="text-gray-400">Reports</span>
        <span className="mx-2 text-gray-400">{">"}</span>
        <span className="border-b border-gray-800 text-gray-800 font-medium">BIR Form No. 0619-E</span>
      </div>

      {/* Header Section */}
      <div className="mb-6">
        <h3 className="text-xl font-bold text-customMainColor uppercase tracking-tight">BIR Form No. 0619-E</h3>
        <p className="text-xs text-gray-400 mt-1 italic">Select parameters to generate the official BIR 0619-E PDF document.</p>
      </div>

      {/* Filter Card */}
      <div className="bg-gray-50 p-5 rounded-xl border border-gray-200 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">

          {/* Column 1: Company Selection (Dynamic) */}
          <div className="w-full flex flex-col justify-center my-2">
            <DynamicFilterBar
              requestedFilters={['COMPANY']}
              activeFilterState={activeFilterState}
              onFilterChange={onFilterChange}
              showButtons={false}
              gridClass="grid grid-cols-1 gap-2 my-2"
            />
          </div>

          {/* Column 2: Reusable Month Selector */}
          <div className="flex flex-col justify-center my-2">
            <ReportSelect
              label="Month"
              placeholder="Select Month"
              value={month}
              onChange={setMonth}
              options={monthsOptions}
            />
          </div>

          {/* Column 3: Reusable Year Selector */}
          <div className="flex flex-col justify-center my-2">
            <ReportSelect
              label="Year"
              value={year}
              onChange={setYear}
              options={yearsOptions}
            />
          </div>
        </div>

        {/* Action Button Row */}
        <div className="flex gap-3 mt-8 border-t border-gray-200 pt-5">
          <Button
            name="GENERATE & DOWNLOAD"
            className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-2.5 rounded-lg font-bold shadow-sm transition-all"
            onClick={handleGenerate}
          />
          <Button
            name="CLEAR"
            className="bg-white border border-gray-300 text-gray-600 hover:bg-gray-100 px-8 py-2.5 rounded-lg font-bold transition-all"
            onClick={handleClear}
          />
        </div>
      </div>

      {/* Global Loading Spinner for the generation process */}
      <LoadingSpinner loading={reportHook.loading || loadingEnums} />
    </div>
  );
};